import { mdiCheck, mdiEmailOutline, mdiPlus, mdiReply } from '@mdi/js';
import Icon from '@mdi/react';
import { format, parse } from 'date-fns';
import _ from 'lodash';
import React from 'react';
import { Row } from 'react-table';
import { MessagePurposeType } from '../../api';
import { MessagingContext } from '../../services';
import { BaseButton, BaseCheckbox, getOptions, isSupport } from '../base';
import {
    DataTableCell,
    DataTablePresenter,
    ITableColumn,
    ITableFetchOptions,
    renderDate,
    renderString,
} from '../data-table';
import { FilterMethod, IFilterSchema } from '../data-table/components/filters/filters.models';
import { DoneButton } from './done-button';
import * as S from './message-list.styles';
import { PendingButton } from './pending-button';
import { UndoneButton } from './undone-button';
import { ProviderDoneButton } from './provider-done-button';
import { getNickname, IMessagingAuthor, IMessagingMessage, isLastAuthor } from './util';

interface IMessageListProps {
    inboxCount: number;
    inboxMessages: IMessagingMessage[];
    doneCount: number;
    doneMessages: IMessagingMessage[];
    onFetchData: (newTableState: ITableFetchOptions) => Promise<void>;
    onShowCompose: () => void;
    selectedMessageId: string | undefined;
    showFiltering: boolean;
    showNavigation: boolean;
    showSettings?: boolean;
    customColumns?: ITableColumn[];
    unreadCount?: number;
}

interface IMessageListState {
    tableState: ITableFetchOptions | undefined;
    selected: string[];
    showDoneMessages: boolean;
    isPending: boolean;
}

// Removed unused date helper functions

export class MessageList extends React.Component<IMessageListProps, IMessageListState> {
    public static contextType = MessagingContext;
    private presenter: DataTablePresenter<IMessagingMessage>;
    private columns: ITableColumn<IMessagingMessage>[] = ([
        {
            id: 'Selected',
            Header: ({ data }): React.ReactElement => {
                        return (
                            <BaseCheckbox
                                disabled={this.state.isPending || (this.context as any)?.isReadOnly}
                                checked={this.isAllSelected(data as IMessagingMessage[])}
                                onChange={this.toggleAll(data as IMessagingMessage[])}
                            />
                        );
            },

            accessor: 'id',
            Cell: ({ cell }): React.ReactElement => (
                        <span onClick={this.clickSelected(cell.value)}>
                            <BaseCheckbox
                                disabled={this.state.isPending || (this.context as any).isReadOnly}
                                checked={this.isSelected(cell.value)}
                                onChange={this.toggleSelected(cell.value)}
                            />
                        </span>
            ),
            maxWidth: 80,
            disableSortBy: true,
        },
        {
            id: 'From',
            Header: 'From',
            accessor: 'from',
            Cell: ({ row }): React.ReactElement => {
                const { comments, author, lastAuthor } = row.original;
                return (
                    <>
                        {this.renderFromField(row.original, author)}
                        {!!comments && comments > 1 && author && lastAuthor && author.id !== lastAuthor.id && (
                            <>
                                <S.ReplyIcon path={mdiReply} size={0.75} />
                                {this.renderFromField(row.original, lastAuthor)}
                            </>
                        )}
                        {!!comments && comments > 1 && <S.CommentCount>{comments}</S.CommentCount>}
                    </>
                );
            },
            maxWidth: 400,
        },
        {
            id: 'Priority',
            Header: 'Priority',
            Cell: ({ row }) => {
                const priority = (row.original as any).priority;
                return renderString(priority ? _.capitalize(priority.toLowerCase()) : '');
            },
            minWidth: 100,
            maxWidth: 120,
            disableSortBy: true,
        },
        {
            id: 'LastAuthor',
            Header: 'Last Author',
            Cell: ({ row }) => {
                const { lastAuthor } = row.original;
                return renderString(getNickname(lastAuthor));
            },
            minWidth: 150,
            maxWidth: 200,
            disableSortBy: true,
        },
        ...(this.props.customColumns || []),
        {
            id: 'Subject',
            Header: 'Subject',
            Cell: ({ row }): React.ReactElement => {
                const { documents, subject, read } = row.original;
                return (
                    <S.SubjectField unread={!read}>
                        {subject}
                        {!!documents && documents > 0 && <S.AttachmentField count={documents} />}
                    </S.SubjectField>
                );
            },
            accessor: 'subject',
            minWidth: 200,
            maxWidth: 9999,
        },
        {
            id: 'Updated',
            Header: 'Updated',
            accessor: 'updated',
            Cell: ({ cell }): string => {
                try {
                    const dateValue = cell.value;
                    if (!dateValue) {
                        return '';
                    }
                    const parsedDate = new Date(dateValue);
                    if (isNaN(parsedDate.getTime())) {
                        console.error('Invalid date value:', dateValue);
                        return '';
                    }
                    return format(parsedDate, 'MMM d, yyyy, h:mm a');
                } catch (error) {
                    console.error('Error formatting date:', error, 'Value:', cell.value);
                    return '';
                }
            },
            maxWidth: 200,
            defaultSort: { id: 'Updated', desc: true },
            show: true,
        },
        {
            id: 'Created',
            Header: 'Created',
            accessor: 'created',
            Cell: ({ cell }): string => {
                try {
                    const dateValue = cell.value;
                    if (!dateValue) {
                        return '';
                    }
                    const parsedDate = new Date(dateValue);
                    if (isNaN(parsedDate.getTime())) {
                        return '';
                    }
                    return format(parsedDate, 'MMM d, yyyy, h:mm a');
                } catch (error) {
                    return '';
                }
            },
            maxWidth: 200,
            show: true,
        },
        {
            id: 'Purpose',
            Header: 'Purpose',
            accessor: 'purposeType',
            Cell: DataTableCell('String', { normalized: true, type: 'String' }),
            minWidth: 180,
            show: false,
        },
    ] as ITableColumn<IMessagingMessage>[]);

    private filterSchemas: IFilterSchema[] = [
        {
            label: 'From',
            filterId: 'filterAuthor',
            filterType: 'String',
            columnId: 'From',
        },
        {
            label: 'Subject',
            filterId: 'filterSubject',
            filterType: 'String',
            columnId: 'Subject',
        },
        {
            label: 'Updated',
            filterId: 'filterUpdated',
            filterType: 'DateTime',
            columnId: 'Updated',
        },
        {
            label: 'Created',
            filterId: 'filterCreated',
            filterType: 'DateTime',
            columnId: 'Created',
        },
        {
            label: 'Read',
            filterId: 'filterRead',
            filterType: 'Select',
            options: [
                {
                    label: 'Yes',
                    value: '1',
                },
                {
                    label: 'No',
                    value: '0',
                },
            ],
            columnId: '',
        },
        {
            label: 'Purpose',
            filterId: 'filterPurpose',
            filterType: 'Select',
            options: getOptions(MessagePurposeType, { normalize: true }),
            columnId: 'purpose',
        },
    ];

    constructor(props: IMessageListProps) {
        super(props);
        this.state = {
            tableState: undefined,
            selected: [],
            showDoneMessages: false,
            isPending: false,
        };
        this.presenter = new DataTablePresenter<IMessagingMessage>({
            name: 'message-list',
            data: [],
            columns: this.columns,
            filterSchemas: this.filterSchemas,
        });
    }

    public componentDidUpdate(): void {
        const { showDoneMessages } = this.state;
        const { doneCount, doneMessages, inboxCount, inboxMessages } = this.props;

        const visibleMessages = showDoneMessages ? doneMessages : inboxMessages;
        const visibleCount = showDoneMessages ? doneCount : inboxCount;
        this.presenter.onDataUpdate(visibleMessages);
        this.presenter.onDataTotalChange(visibleCount);
    }

    public render(): React.ReactElement {
        const { selected, showDoneMessages, isPending } = this.state;
        const {
            doneCount,
            inboxCount,
            unreadCount,
            onShowCompose,
            showFiltering,
            showNavigation,
            showSettings,
        } = this.props;

        return (
            <S.MessageList>
                <S.MessageControls>
                    <S.MessageControl>
                        {!!selected.length && (
                            <S.ControlsWrapper>
                                {isPending && <PendingButton />}
                                {!isPending && !showDoneMessages && (
                                    <>
                                        <DoneButton onClick={this.markSelectedDone} count={selected.length} />
                                        <span style={{ marginLeft: 8 }}>
                                            <ProviderDoneButton
                                                onClick={this.markSelectedProviderDone}
                                                count={selected.length}
                                            />
                                        </span>
                                    </>
                                )}
                                {!isPending && showDoneMessages && (
                                    <UndoneButton onClick={this.markSelectedUndone} count={selected.length} />
                                )}
                            </S.ControlsWrapper>
                        )}

                        <S.TabsContainer>
                            <BaseButton
                                variant="outlined"
                                disabled={!showDoneMessages}
                                onClick={this.toggleDone(false)}>
                                <Icon path={mdiEmailOutline} size={0.75} />
                                <span>Inbox</span>
                                <S.ControlCount>{inboxCount}</S.ControlCount>
                            </BaseButton>
                            <BaseButton variant="outlined" disabled={showDoneMessages} onClick={this.toggleDone(true)}>
                                <Icon path={mdiCheck} size={0.75} />
                                <span>Done</span>
                            </BaseButton>
                        </S.TabsContainer>

                        {!!unreadCount && !this.hasFilter && (
                            <S.UnreadButton variant="outlined" onClick={this.showUnread}>
                                Show {unreadCount} unread
                            </S.UnreadButton>
                        )}
                    </S.MessageControl>
                    <S.MessageControl>
                        <S.ComposeButton disabled={(this.context as any).isReadOnly} variant="primary" onClick={onShowCompose}>
                            <S.ComposeIcon path={mdiPlus} size={0.75} />
                            <S.ComposeLabel>New message</S.ComposeLabel>
                        </S.ComposeButton>
                    </S.MessageControl>
                </S.MessageControls>
                <S.MessageTable
                    presenter={this.presenter}
                    isLoading={(this.context as any).isListLoading}
                    onFetch={this.handleFetch}
                    onRowClick={this.onRowClick()}
                    rowClass={this.rowClass()}
                    hideFiltering={!showFiltering}
                    hideNavigation={!showNavigation}
                    hideSettings={!showSettings}
                    showExport={true}
                />
                <S.SelectedMessageRow show={!!this.props.selectedMessageId} />
            </S.MessageList>
        );
    }

    private get hasFilter() {
        return !!_.find(this.presenter.filters, { filterId: 'filterRead' });
    }

    private handleFetch = (options: ITableFetchOptions): Promise<void> => {
        const { showDoneMessages } = this.state;

        // Ensure the correct open/done filter is applied based on the active tab.
        let validFilters = options.validFilters || [];

        if (showDoneMessages) {
            // When viewing "Done", always force filterOpen=0 so the backend
            // returns done messages and the parent updates `doneMessages`.
            validFilters = [
                ...validFilters.filter(f => f.filterId !== 'filterOpen'),
                {
                    filterId: 'filterOpen',
                    filterMethod: FilterMethod.EQ,
                    filterType: 'String',
                    value: '0',
                },
            ];
        }

        return this.props.onFetchData({
            ...options,
            validFilters,
        });
    };

    private rowClass(): (row: Row<IMessagingMessage>) => string {
        return (row: Row<IMessagingMessage>): string => {
            if (row.original.id === this.props.selectedMessageId) {
                return _.get(S.SelectedMessageRow, 'componentStyle.lastClassName');
            }
            return '';
        };
    }

    private onRowClick(): (row: Row) => Promise<void> {
        const { onMessageClose, onMessageOpen } = this.context as any;
        const { selectedMessageId } = this.props;
        return async (row: Row<IMessagingMessage>): Promise<void> => {
            const {
                original: { id },
            } = row;
            if (id === selectedMessageId) {
                await onMessageClose();
            } else {
                await (onMessageOpen && onMessageOpen(id));
            }
        };
    }

    private markSelectedDone = async (): Promise<void> => {
        this.setState({ isPending: true });
        if (_.includes(this.state.selected, this.props.selectedMessageId)) {
            await (this.context as any).onMessageClose();
        }
        await (this.context as any).onMarkAsClosed(this.state.selected);
        this.setState({
            isPending: false,
            selected: [],
        });
    };
    private markSelectedUndone = async (): Promise<void> => {
        this.setState({ isPending: true });
        await (this.context as any).onMarkAsOpen(this.state.selected);
        this.setState({
            isPending: false,
            selected: [],
        });
    };
    private markSelectedProviderDone = async (): Promise<void> => {
        const context: any = this.context as any;
        if (!context.onProviderMarkAsClosed) {
            return;
        }
        this.setState({ isPending: true });
        if (_.includes(this.state.selected, this.props.selectedMessageId)) {
            await context.onMessageClose();
        }
        const ids = _.concat([], this.state.selected);
        await Promise.all(
            _.map(ids, async (id: string) => {
                await context.onProviderMarkAsClosed(id);
            }),
        );
        this.setState({
            isPending: false,
            selected: [],
        });
    };
    private clickSelected = (id: string): ((e: React.MouseEvent) => void) => {
        return (e: React.MouseEvent): void => {
            e.stopPropagation();
            e.preventDefault();
            this.toggleSelected(id)();
        };
    };

    private isSelected(id: string): boolean {
        return _.includes(this.state.selected, id);
    }
    private isAllSelected(data: IMessagingMessage[]): boolean {
        return data.length === this.state.selected.length;
    }
    private toggleAll(data: IMessagingMessage[]): () => void {
        return (): void => {
            if (!this.state.selected.length) {
                this.setState({ selected: _.compact(_.map(data, 'id')) });
            } else {
                this.setState({ selected: [] });
            }
        };
    }
    private toggleSelected(id: string): () => void {
        return (): void => {
            if (this.isSelected(id)) {
                this.setState({ selected: _.without(this.state.selected, id) });
            } else {
                this.setState({ selected: _.concat(this.state.selected, id) });
            }
        };
    }
    private toggleDone(newValue: boolean): () => void {
        return (): void => {
            if (newValue !== this.state.showDoneMessages) {
                this.setState({ showDoneMessages: newValue, selected: [] });
                // Always trigger fetch when switching tabs to ensure correct filters are applied
                const tableDefaults = this.presenter.getTableDefaults();
                // Remove any existing filterOpen filter and add the correct one based on tab
                const filtersWithoutOpen = (this.presenter.validFilters || []).filter(
                    f => f.filterId !== 'filterOpen',
                );
                this.props.onFetchData({
                    pageIndex: tableDefaults?.pageIndex || 0,
                    pageSize: tableDefaults?.pageSize || 100,
                    sortBy: tableDefaults?.sortBy || [],
                    validFilters: [
                        ...filtersWithoutOpen,
                        {
                            filterId: 'filterOpen',
                            filterMethod: FilterMethod.EQ,
                            filterType: 'String',
                            value: newValue ? '0' : '1', // Done: '0', Inbox: '1'
                        },
                    ],
                });
            }
        };
    }
    private readonly showUnread = (): void => {
        // Update filterRead in presenter
        this.presenter.findAndUpdateFilter(
            { filterId: 'filterRead' },
            {
                filterId: 'filterRead',
                filterType: 'Select',
                filterMethod: FilterMethod.EQ,
                value: {
                    value: '0',
                    label: 'No',
                },
            },
        );
        
        // DataTable will automatically trigger fetch when validFilters changes
        // No need to manually trigger fetch here
    };

    private renderFromField(message: IMessagingMessage, author: IMessagingAuthor | undefined): React.ReactElement {
        return (
            <>
                <S.IndicatorField unread={!message.read && !!author && isLastAuthor(message, author)} author={author} />
                <S.FromField unread={!message.read && !!author && isLastAuthor(message, author)}>
                    {getNickname(author)}
                </S.FromField>
            </>
        );
    }
}
